import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Package, Save, X, Edit2, Trash2, Tag, Archive,
  SlidersHorizontal, History, AlertTriangle, TrendingDown, TrendingUp, CheckCircle2,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Layers,
  Boxes, ArrowUpRight, ArrowDownRight, RefreshCw, DollarSign, ClipboardCheck,
  ShoppingCart
} from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader';
import { toast } from 'react-toastify';
import { useAuthToken } from '../../../lib/useAuthToken';
import {
  useItems, useCreateItem, useUpdateItem, useDeleteItem, useAdjustStock, useInventoryCategories
} from '../hooks/useInventory';
import { CostHistoryModal } from '../components/CostHistoryModal';
import { CategoryManagerModal } from '../components/CategoryManagerModal';
import { KardexModal } from '../components/KardexModal';
import { WasteLogModal } from '../components/WasteLogModal';
import { ReplenishmentModal } from '../components/ReplenishmentModal';

const UNITS = ['g', 'kg', 'ml', 'litros', 'unidades', 'cajas', 'porciones', 'libras', 'paquetes', 'botellas'];
const QUICK_PACKAGES = ['Paquete', 'Paca', 'Caja', 'Bulto', 'Sixpack', 'Bolsa', 'Botella', 'Galón', 'Lata'];
const PAGE_SIZES = [12, 24, 48, 96];

const QUICK_REASONS = [
  'Conteo físico / Cuadre',
  'Compra sin factura',
  'Merma / Vencimiento',
  'Deterioro / Desperdicio',
  'Consumo interno / Degustación',
];

const ItemsList: React.FC = () => {
  const t = useAuthToken();
  const { data: pageData, isLoading, refetch } = useItems({ limit: 1000 });
  const createMut = useCreateItem();
  const updateMut = useUpdateItem();
  const deleteMut = useDeleteItem();
  const adjustStockMut = useAdjustStock();

  const items = pageData?.data ?? [];
  const { data: dbCategories = [] } = useInventoryCategories();

  // Filtros y Búsqueda
  const [q, setQ] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('TODAS');
  const [stockStatusFilter, setStockStatusFilter] = useState<'ALL' | 'LOW' | 'OUT' | 'OK'>('ALL');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);

  // Estados de modales
  const [editing, setEditing] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<any>(null);
  const [historyItem, setHistoryItem] = useState<any>(null);
  const [stockAdjustItem, setStockAdjustItem] = useState<any>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [kardexItemId, setKardexItemId] = useState<number | string | null>(null);
  const [isWasteModalOpen, setIsWasteModalOpen] = useState(false);
  const [wasteDefaultItem, setWasteDefaultItem] = useState<number | string | null>(null);
  const [isReplenishmentOpen, setIsReplenishmentOpen] = useState(false);

  // Estado del formulario de Ajuste de Stock
  const [adjustMode, setAdjustMode] = useState<'COUNT' | 'IN' | 'OUT'>('COUNT');
  const [adjustValue, setAdjustValue] = useState<string>('');
  const [adjustReason, setAdjustReason] = useState<string>('Conteo físico / Cuadre');

  // Form state de creación/edición de ítem
  const [form, setForm] = useState({
    sku: '', name: '', category: '', unit: 'unidades', min_stock: 0, cost: 0, notes: '',
    purchase_unit: '', conversion_factor: 1, cost_change_reason: '',
    purchase_price: 0, last_purchase_price: null as number | null
  });

  const resetForm = () => {
    setForm({
      sku: '', name: '', category: '', unit: 'unidades', min_stock: 0, cost: 0, notes: '',
      purchase_unit: '', conversion_factor: 1, cost_change_reason: '',
      purchase_price: 0, last_purchase_price: null
    });
    setEditing(null);
    setIsNew(false);
  };

  const handleEdit = (i: any) => {
    setEditing(i);
    const unitCost = Number(i.cost || 0);
    const factor = i.conversion_factor !== undefined ? Number(i.conversion_factor) : 1;
    const pPrice = (i.purchase_price !== null && i.purchase_price !== undefined && Number(i.purchase_price) > 0)
      ? Number(i.purchase_price)
      : (i.purchase_unit && factor > 1 ? Number((unitCost * factor).toFixed(2)) : 0);

    setForm({
      sku: i.sku || '',
      name: i.name || '',
      category: i.category || '',
      unit: i.unit || 'unidades',
      min_stock: i.min_stock || 0,
      cost: unitCost,
      notes: i.notes || '',
      purchase_unit: i.purchase_unit || '',
      conversion_factor: factor,
      purchase_price: pPrice,
      last_purchase_price: (i.last_purchase_price !== null && i.last_purchase_price !== undefined) ? Number(i.last_purchase_price) : null,
      cost_change_reason: ''
    });
    setIsNew(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sincronización inteligente de empaque y costo
  const handlePurchasePriceChange = (newPrice: number) => {
    setForm(f => {
      const factor = Number(f.conversion_factor) || 1;
      const calculatedCost = factor > 0 ? Number((newPrice / factor).toFixed(4)) : f.cost;
      return {
        ...f,
        purchase_price: newPrice,
        cost: calculatedCost
      };
    });
  };

  const handleConversionFactorChange = (newFactor: number) => {
    setForm(f => {
      const pPrice = Number(f.purchase_price) || 0;
      let calculatedCost = f.cost;
      if (pPrice > 0 && newFactor > 0) {
        calculatedCost = Number((pPrice / newFactor).toFixed(4));
      } else if (f.cost > 0 && newFactor > 0) {
        return {
          ...f,
          conversion_factor: newFactor,
          purchase_price: Number((f.cost * newFactor).toFixed(2))
        };
      }
      return {
        ...f,
        conversion_factor: newFactor,
        cost: calculatedCost
      };
    });
  };

  const handleCostChange = (newCost: number) => {
    setForm(f => {
      const factor = Number(f.conversion_factor) || 1;
      const calculatedPPrice = (f.purchase_unit && factor > 1)
        ? Number((newCost * factor).toFixed(2))
        : f.purchase_price;
      return {
        ...f,
        cost: newCost,
        purchase_price: calculatedPPrice
      };
    });
  };

  const handlePurchaseUnitSelect = (pkg: string) => {
    setForm(f => ({
      ...f,
      purchase_unit: pkg
    }));
  };

  // Comparativa en vivo de precio de compra ("¿Más barato o más caro?")
  const priceComparison = useMemo(() => {
    const currentPrice = Number(form.purchase_price || 0);
    const refPrice = form.last_purchase_price !== null
      ? Number(form.last_purchase_price)
      : (editing && editing.purchase_price ? Number(editing.purchase_price) : null);

    if (!refPrice || refPrice <= 0 || !currentPrice || currentPrice <= 0) return null;
    const diff = currentPrice - refPrice;
    if (Math.abs(diff) < 0.01) {
      return { trend: 'equal', diff: 0, pct: 0, refPrice };
    }
    const pct = Number(((diff / refPrice) * 100).toFixed(1));
    return {
      trend: diff > 0 ? 'up' : 'down',
      diff: Math.abs(diff),
      pct: Math.abs(pct),
      refPrice
    };
  }, [form.purchase_price, form.last_purchase_price, editing]);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('El nombre es obligatorio');
    if (!form.unit.trim()) return toast.error('La unidad de medida es obligatoria');

    const payload: any = {
      ...form,
      min_stock: Number(form.min_stock) || 0,
      cost: Number(form.cost) || 0,
      conversion_factor: Number(form.conversion_factor) || 1,
      purchase_price: Number(form.purchase_price) || null,
      last_purchase_price: form.last_purchase_price !== null ? Number(form.last_purchase_price) : null,
    };

    if (!payload.sku) {
      delete payload.sku;
    }

    if (!isNew && editing && Number(editing.cost) !== payload.cost) {
      payload.cost_change_reason = form.cost_change_reason.trim() || 'Modificación manual de costo en catálogo';
    }

    try {
      if (isNew) {
        await createMut.mutateAsync(payload);
        toast.success('Insumo creado correctamente');
      } else {
        await updateMut.mutateAsync({ id: editing.id, data: payload });
        toast.success('Insumo actualizado');
      }
      resetForm();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al guardar el insumo');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      toast.success('Insumo eliminado');
      setConfirmDelete(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al eliminar');
    }
  };

  // Abrir modal de Ajuste de Stock
  const handleOpenStockAdjust = (item: any) => {
    const currentStock = Number(item.current_stock ?? 0);
    setStockAdjustItem(item);
    setAdjustMode('COUNT');
    setAdjustValue(currentStock >= 0 ? currentStock.toString() : '0');
    setAdjustReason('Conteo físico / Cuadre');
  };

  // Guardar Corrección de Stock
  const handleSaveStockAdjust = async () => {
    if (!stockAdjustItem) return;
    const current = Number(stockAdjustItem.current_stock ?? 0);
    const parsedVal = parseFloat(adjustValue.replace(',', '.'));

    if (isNaN(parsedVal) || parsedVal < 0) {
      return toast.error('Por favor ingresa una cantidad numérica válida mayor o igual a cero');
    }

    let calculatedNewStock = current;
    if (adjustMode === 'COUNT') {
      calculatedNewStock = parsedVal;
    } else if (adjustMode === 'IN') {
      calculatedNewStock = current + parsedVal;
    } else if (adjustMode === 'OUT') {
      calculatedNewStock = Math.max(0, current - parsedVal);
    }

    const diff = calculatedNewStock - current;
    if (Math.abs(diff) < 0.0001) {
      toast.info('No hay variación en el stock actual');
      setStockAdjustItem(null);
      return;
    }

    try {
      await adjustStockMut.mutateAsync({
        id: stockAdjustItem.id,
        data: {
          new_stock: calculatedNewStock,
          reason: adjustReason.trim() || 'Ajuste de stock físico',
          type: adjustMode
        }
      });

      toast.success(
        `¡Stock de "${stockAdjustItem.name}" actualizado a ${calculatedNewStock.toFixed(2)} ${stockAdjustItem.unit}!`
      );
      setStockAdjustItem(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al guardar el ajuste de stock');
    }
  };

  // Categorías combinadas (Base de datos + ítems)
  const categories = useMemo(() => {
    const set = new Set<string>();
    dbCategories.forEach((c: any) => {
      if (c.name && c.name.trim()) set.add(c.name.trim());
    });
    items.forEach((i: any) => {
      if (i.category && i.category.trim()) {
        set.add(i.category.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [dbCategories, items]);

  // Filtrado
  const filtered = useMemo(() => {
    return items.filter((i: any) => {
      // Búsqueda texto
      const matchesQ =
        !q ||
        i.name.toLowerCase().includes(q.toLowerCase()) ||
        i.sku?.toLowerCase().includes(q.toLowerCase()) ||
        i.category?.toLowerCase().includes(q.toLowerCase());

      if (!matchesQ) return false;

      // Filtro de Categoría
      if (selectedCategory !== 'TODAS' && (i.category || '').trim().toLowerCase() !== selectedCategory.toLowerCase()) {
        return false;
      }

      // Filtro de Estado de Stock
      const stock = Number(i.current_stock ?? 0);
      const min = Number(i.min_stock ?? 0);

      if (stockStatusFilter === 'OUT' && stock > 0) return false;
      if (stockStatusFilter === 'LOW' && (stock <= 0 || stock > min)) return false;
      if (stockStatusFilter === 'OK' && stock <= min) return false;

      return true;
    });
  }, [items, q, selectedCategory, stockStatusFilter]);

  // Métricas rápidas para KPIs
  const stats = useMemo(() => {
    let outCount = 0;
    let lowCount = 0;
    let okCount = 0;
    let estimatedValue = 0;

    items.forEach((i: any) => {
      const stock = Number(i.current_stock ?? 0);
      const min = Number(i.min_stock ?? 0);
      const cost = Number(i.cost ?? 0);

      if (stock <= 0) outCount++;
      else if (stock <= min) lowCount++;
      else okCount++;

      if (stock > 0 && cost > 0) {
        estimatedValue += stock * cost;
      }
    });

    return { total: items.length, outCount, lowCount, okCount, estimatedValue };
  }, [items]);

  // Paginación calculada
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedItems = useMemo(() => {
    const start = (validCurrentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, validCurrentPage, pageSize]);

  // Reiniciar a página 1 cuando cambian los filtros
  const handleFilterChange = (setter: () => void) => {
    setter();
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-6">

        {/* ── Header ── */}
        <PageHeader
          title="Ítems / Insumos"
          subtitle="Control de existencias físicas, costos históricos y correcciones de inventario en tiempo real"
          actions={
            <>
              <button
                onClick={() => refetch()}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-divider bg-surface text-muted hover:text-[color:var(--color-text)] hover:bg-surface-muted transition shadow-sm"
                title="Refrescar catálogo"
              >
                <RefreshCw size={17} />
              </button>
              <button
                onClick={() => setIsCategoryModalOpen(true)}
                className="inline-flex h-10 sm:h-11 items-center gap-2 rounded-xl border border-divider bg-surface px-3.5 sm:px-4 text-xs sm:text-sm font-bold text-[color:var(--color-text)] hover:bg-surface-muted transition shadow-sm"
                title="Administrar categorías oficiales de insumos"
              >
                <Tag size={17} className="text-primary" /> Categorías
              </button>
              <Link
                to="/inventory/audit"
                className="inline-flex h-10 sm:h-11 items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 text-primary px-3.5 sm:px-4 text-xs sm:text-sm font-bold hover:bg-primary/20 transition shadow-sm"
                title="Abrir planilla de auditoría para contar stock físico rápidamente"
              >
                <ClipboardCheck size={17} /> Toma Física
              </Link>
              <button
                onClick={() => setIsReplenishmentOpen(true)}
                className="inline-flex h-10 sm:h-11 items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-600 px-3.5 sm:px-4 text-xs sm:text-sm font-bold hover:bg-amber-500/20 transition shadow-sm"
                title="Generar órdenes de compra automáticas para insumos bajo stock mínimo"
              >
                <ShoppingCart size={17} /> Sugerir Compras
              </button>
              <button
                onClick={() => { setWasteDefaultItem(null); setIsWasteModalOpen(true); }}
                className="inline-flex h-10 sm:h-11 items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-600 px-3.5 sm:px-4 text-xs sm:text-sm font-bold hover:bg-red-500/20 transition shadow-sm"
                title="Registrar mermas, caídas o desperdicios en cocina/barra"
              >
                <Trash2 size={17} /> Mermas
              </button>
              <button
                onClick={() => { resetForm(); setIsNew(true); }}
                className="inline-flex h-10 sm:h-11 items-center gap-2 rounded-xl bg-primary px-4 sm:px-6 text-xs sm:text-sm font-bold text-white shadow-md hover:opacity-90 active:scale-95 transition"
              >
                <Plus size={18} /> Nuevo Insumo
              </button>
            </>
          }
        />

        {/* ── KPIs / Métricas Rápidas ── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-divider bg-surface p-4 shadow-sm">
            <div className="flex items-center justify-between text-muted">
              <span className="text-xs font-semibold uppercase tracking-wider">Total Insumos</span>
              <Boxes size={18} className="text-primary" />
            </div>
            <p className="mt-2 text-2xl font-bold text-[color:var(--color-text)]">{stats.total}</p>
            <p className="text-[11px] text-muted mt-0.5">{categories.length} categorías registradas</p>
          </div>

          <div className="rounded-2xl border border-divider bg-surface p-4 shadow-sm">
            <div className="flex items-center justify-between text-muted">
              <span className="text-xs font-semibold uppercase tracking-wider">Agotados / Cero</span>
              <AlertTriangle size={18} className="text-red-500" />
            </div>
            <p className={`mt-2 text-2xl font-bold ${stats.outCount > 0 ? 'text-red-600' : 'text-[color:var(--color-text)]'}`}>
              {stats.outCount}
            </p>
            <p className="text-[11px] text-muted mt-0.5">Requieren compra urgente</p>
          </div>

          <div className="rounded-2xl border border-divider bg-surface p-4 shadow-sm">
            <div className="flex items-center justify-between text-muted">
              <span className="text-xs font-semibold uppercase tracking-wider">Bajo Mínimo</span>
              <TrendingDown size={18} className="text-amber-500" />
            </div>
            <p className={`mt-2 text-2xl font-bold ${stats.lowCount > 0 ? 'text-amber-600' : 'text-[color:var(--color-text)]'}`}>
              {stats.lowCount}
            </p>
            <p className="text-[11px] text-muted mt-0.5">Por debajo de alerta mínima</p>
          </div>

          <div className="rounded-2xl border border-divider bg-surface p-4 shadow-sm">
            <div className="flex items-center justify-between text-muted">
              <span className="text-xs font-semibold uppercase tracking-wider">Valor Estimado</span>
              <DollarSign size={18} className="text-emerald-500" />
            </div>
            <p className="mt-2 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              ${stats.estimatedValue.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
            </p>
            <p className="text-[11px] text-muted mt-0.5">Costo total insumos en stock</p>
          </div>
        </div>

        {/* ── Formulario de Creación / Edición ── */}
        {(isNew || editing) && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:p-6 shadow-md animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white">
                  {isNew ? <Plus size={20} /> : <Edit2 size={18} />}
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-[color:var(--color-text)]">
                    {isNew ? 'Registrar Nuevo Insumo' : `Editando: ${editing.name}`}
                  </h2>
                  <p className="text-xs text-muted">
                    {isNew ? 'Define los datos base del insumo y su unidad de medida' : 'Modifica parámetros, factores de conversión o costo unitario'}
                  </p>
                </div>
              </div>
              <button
                onClick={resetForm}
                className="rounded-full p-2 text-muted hover:bg-surface hover:text-red-500 transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Nombre *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Coca Cola PET / Queso Mozzarella"
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">
                  SKU / Código {isNew ? '(opcional)' : ''}
                </label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={e => setForm({ ...form, sku: e.target.value })}
                  placeholder="Autogenerado si está vacío"
                  disabled={!isNew}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60 shadow-sm"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-muted">Categoría</label>
                  <button
                    type="button"
                    onClick={() => setIsCategoryModalOpen(true)}
                    className="text-[11px] font-bold text-primary hover:underline flex items-center gap-0.5"
                  >
                    <Plus size={12} /> Gestionar / Nueva
                  </button>
                </div>
                <select
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                >
                  <option value="">-- Sin categoría asignada --</option>
                  {categories.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Unidad de Medida *</label>
                <select
                  value={form.unit}
                  onChange={e => setForm({ ...form, unit: e.target.value })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                >
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Stock Mínimo (Alerta)</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.min_stock}
                  onChange={e => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">
                  Costo Unitario ($) *
                  {form.purchase_unit && form.conversion_factor > 1 && (
                    <span className="ml-1 text-[10px] text-primary font-normal">
                      (o usa el empaque abajo)
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.cost || ''}
                  onChange={e => handleCostChange(parseFloat(e.target.value) || 0)}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                  placeholder="Costo por unidad base"
                />
                <span className="text-[10px] text-muted block mt-1">
                  Costo por 1 {form.unit || 'unidad'} (para recetas y balance)
                </span>
              </div>

              {!isNew && editing && Number(editing.cost) !== Number(form.cost) && (
                <div className="sm:col-span-2 lg:col-span-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                  <label className="mb-1 block text-xs font-bold text-amber-800 dark:text-amber-300">
                    Motivo del cambio de costo (quedará registrado en el historial):
                  </label>
                  <input
                    type="text"
                    value={form.cost_change_reason}
                    onChange={e => setForm({ ...form, cost_change_reason: e.target.value })}
                    placeholder="Ej: Aumento de precio del proveedor, cambio de marca, etc."
                    className="w-full rounded-lg border border-amber-300 bg-surface px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              )}

              {/* ── Sección de Empaque y Presentación de Compra (Opcional) ── */}
              <div className="sm:col-span-2 lg:col-span-3 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/5 via-surface to-primary/5 p-4 sm:p-5 space-y-3.5 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Package size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[color:var(--color-text)]">
                        Empaque y Presentación de Compra (Opcional)
                      </h4>
                      <p className="text-[11px] text-muted">
                        Si compras por paquete, paca, caja, bulto o sixpack, calcula la unidad y compara precios automáticamente
                      </p>
                    </div>
                  </div>

                  {/* Chips rápidos de empaque común */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[10.5px] text-muted font-medium mr-1">Rápido:</span>
                    {QUICK_PACKAGES.map(pkg => (
                      <button
                        key={pkg}
                        type="button"
                        onClick={() => handlePurchaseUnitSelect(pkg)}
                        className={`px-2 py-0.5 rounded-lg text-[11px] font-semibold border transition ${
                          form.purchase_unit.toLowerCase() === pkg.toLowerCase()
                            ? 'bg-primary text-white border-primary shadow-xs'
                            : 'bg-surface text-muted border-divider hover:border-primary/40 hover:text-primary'
                        }`}
                      >
                        {pkg}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3 pt-1">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-muted">
                      Presentación (Empaque)
                    </label>
                    <input
                      type="text"
                      value={form.purchase_unit}
                      onChange={e => setForm({ ...form, purchase_unit: e.target.value })}
                      placeholder="Ej: Paquete, Paca, Caja, Bulto"
                      className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                    />
                    <span className="text-[10px] text-muted block mt-1">
                      Nombre del empaque en la factura
                    </span>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-muted">
                      Precio de Compra del Empaque ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={form.purchase_price || ''}
                      onChange={e => handlePurchasePriceChange(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 3700"
                      className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                    />
                    <span className="text-[10px] text-muted block mt-1">
                      Precio total que pagas por el empaque
                    </span>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-muted">
                      Contenido por Empaque ({form.unit})
                    </label>
                    <input
                      type="number"
                      min="0.0001"
                      step="any"
                      value={form.conversion_factor || ''}
                      onChange={e => handleConversionFactorChange(parseFloat(e.target.value) || 1)}
                      placeholder="Ej: 180"
                      className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                    />
                    <span className="text-[10px] text-muted block mt-1">
                      Unidades que vienen dentro del empaque
                    </span>
                  </div>
                </div>

                {/* Banner de cálculo inteligente y semáforo de referencia */}
                {(form.purchase_unit || (form.purchase_price && form.purchase_price > 0)) && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl bg-surface/90 backdrop-blur-xs p-3 border border-divider text-xs shadow-xs">
                      <div className="flex items-center gap-2">
                        <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary tracking-wider">
                          Cálculo en vivo
                        </span>
                        <span className="text-[color:var(--color-text)] font-medium">
                          1 {form.purchase_unit || 'Empaque'} de <strong>${(form.purchase_price || 0).toLocaleString('es-CO')}</strong> ÷ <strong>{form.conversion_factor} {form.unit}</strong> = <strong className="text-emerald-600 dark:text-emerald-400 font-extrabold text-sm">${form.cost}</strong> por {form.unit}
                        </span>
                      </div>

                      {/* Semáforo comparativo si subió o bajó */}
                      {priceComparison && (
                        <div className="shrink-0">
                          {priceComparison.trend === 'up' && (
                            <span className="inline-flex items-center gap-1 rounded-lg bg-red-100 dark:bg-red-950/40 px-2.5 py-1 text-xs font-bold text-red-700 dark:text-red-400 border border-red-300">
                              <TrendingUp size={13} className="text-red-600" />
                              🔺 +${priceComparison.diff.toLocaleString('es-CO')} (+{priceComparison.pct}%) ¡Más caro vs ref. de ${priceComparison.refPrice.toLocaleString('es-CO')}!
                            </span>
                          )}
                          {priceComparison.trend === 'down' && (
                            <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-400 border border-emerald-300">
                              <TrendingDown size={13} className="text-emerald-600" />
                              🟢 -${priceComparison.diff.toLocaleString('es-CO')} (-{priceComparison.pct}%) ¡Más barato vs ref. de ${priceComparison.refPrice.toLocaleString('es-CO')}!
                            </span>
                          )}
                          {priceComparison.trend === 'equal' && (
                            <span className="inline-flex items-center gap-1 rounded-lg bg-blue-50 dark:bg-blue-950/40 px-2.5 py-1 text-xs font-bold text-blue-700 dark:text-blue-400 border border-blue-200">
                              <CheckCircle2 size={13} className="text-blue-600" />
                              ✅ Mismo precio de referencia (${priceComparison.refPrice.toLocaleString('es-CO')})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="sm:col-span-2 lg:col-span-3">
                <label className="mb-1 block text-xs font-semibold text-muted">Notas Adicionales</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ubicación, proveedor frecuente..."
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-3 pt-3 border-t border-divider">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-xs font-bold text-muted hover:text-[color:var(--color-text)] transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2 text-xs font-bold text-white shadow-md hover:opacity-90 disabled:opacity-60 transition"
              >
                <Save size={15} />
                {createMut.isPending || updateMut.isPending ? 'Guardando...' : 'Guardar Insumo'}
              </button>
            </div>
          </div>
        )}

        {/* ── Barra de Búsqueda y Filtros ── */}
        <div className="rounded-2xl border border-divider bg-surface p-4 shadow-sm space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Buscador */}
            <div className="relative flex-1 max-w-md">
              <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                value={q}
                onChange={e => handleFilterChange(() => setQ(e.target.value))}
                placeholder="Buscar por nombre, SKU o categoría..."
                className="w-full rounded-xl border border-divider bg-surface-muted/50 px-10 py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-inner"
              />
              {q && (
                <button
                  onClick={() => handleFilterChange(() => setQ(''))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-[color:var(--color-text)]"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Filtros de Estado de Stock */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
              <button
                onClick={() => handleFilterChange(() => setStockStatusFilter('ALL'))}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition whitespace-nowrap ${
                  stockStatusFilter === 'ALL'
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-surface-muted text-muted hover:text-[color:var(--color-text)]'
                }`}
              >
                Todos ({items.length})
              </button>
              <button
                onClick={() => handleFilterChange(() => setStockStatusFilter('OUT'))}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                  stockStatusFilter === 'OUT'
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                }`}
              >
                <AlertTriangle size={12} /> Agotados ({stats.outCount})
              </button>
              <button
                onClick={() => handleFilterChange(() => setStockStatusFilter('LOW'))}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                  stockStatusFilter === 'LOW'
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
                }`}
              >
                <TrendingDown size={12} /> Bajos ({stats.lowCount})
              </button>
              <button
                onClick={() => handleFilterChange(() => setStockStatusFilter('OK'))}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                  stockStatusFilter === 'OK'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200'
                }`}
              >
                <CheckCircle2 size={12} /> Óptimos ({stats.okCount})
              </button>
            </div>
          </div>

          {/* Chips de Categorías */}
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pt-2 border-t border-divider/60 pb-1">
              <span className="text-[11px] font-bold text-muted uppercase tracking-wider shrink-0 mr-1 flex items-center gap-1">
                <Tag size={12} /> Categoría:
              </span>
              <button
                onClick={() => handleFilterChange(() => setSelectedCategory('TODAS'))}
                className={`rounded-lg px-2.5 py-1 text-xs font-semibold shrink-0 transition ${
                  selectedCategory === 'TODAS'
                    ? 'bg-primary/20 text-primary font-bold border border-primary/30'
                    : 'text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)]'
                }`}
              >
                Todas
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleFilterChange(() => setSelectedCategory(cat))}
                  className={`rounded-lg px-2.5 py-1 text-xs font-semibold shrink-0 transition ${
                    selectedCategory.toLowerCase() === cat.toLowerCase()
                      ? 'bg-primary/20 text-primary font-bold border border-primary/30'
                      : 'text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Barra Superior de Paginación y Conteo ── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted">
          <div className="flex items-center gap-2">
            <span>
              Mostrando <strong className="text-[color:var(--color-text)]">
                {filtered.length === 0 ? 0 : (validCurrentPage - 1) * pageSize + 1}
              </strong> al <strong className="text-[color:var(--color-text)]">
                {Math.min(validCurrentPage * pageSize, filtered.length)}
              </strong> de <strong className="text-[color:var(--color-text)]">{filtered.length}</strong> insumos filtrados
            </span>
          </div>

          {/* Selector de Tamaño de Página */}
          <div className="flex items-center gap-2">
            <span>Por página:</span>
            <div className="flex items-center rounded-xl border border-divider bg-surface p-0.5 shadow-sm">
              {PAGE_SIZES.map(sz => (
                <button
                  key={sz}
                  onClick={() => { setPageSize(sz); setCurrentPage(1); }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                    pageSize === sz
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted hover:text-[color:var(--color-text)]'
                  }`}
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Cuadrícula de Insumos ── */}
        {isLoading ? (
          <div className="py-16 text-center text-muted space-y-3">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent mx-auto" />
            <p className="text-sm font-semibold">Cargando catálogo de insumos...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-divider bg-surface p-12 text-center shadow-sm">
            <Package size={48} className="mx-auto mb-3 text-muted opacity-30" />
            <h3 className="text-base font-bold text-[color:var(--color-text)]">No se encontraron insumos</h3>
            <p className="text-xs text-muted max-w-sm mx-auto mt-1 mb-4">
              No hay insumos que coincidan con los criterios de búsqueda o filtros seleccionados.
            </p>
            <button
              onClick={() => {
                setQ('');
                setSelectedCategory('TODAS');
                setStockStatusFilter('ALL');
              }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-divider bg-surface px-4 py-2 text-xs font-bold text-primary hover:bg-surface-muted transition shadow-sm"
            >
              Restablecer filtros
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-divider bg-surface shadow-sm">
            <table className="w-full text-left text-sm text-[color:var(--color-text)] whitespace-nowrap">
              <thead className="bg-surface-muted text-xs uppercase text-muted">
                <tr>
                  <th className="px-4 py-3.5 font-semibold">Insumo</th>
                  <th className="px-4 py-3.5 font-semibold">Categoría</th>
                  <th className="px-4 py-3.5 font-semibold">Stock Actual</th>
                  <th className="px-4 py-3.5 font-semibold">Costo Unit.</th>
                  <th className="px-4 py-3.5 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {paginatedItems.map((item: any) => {
                  const currentStock = Number(item.current_stock ?? 0);
                  const minStock = Number(item.min_stock ?? 0);
                  const isOut = currentStock <= 0;
                  const isLow = !isOut && currentStock <= minStock;

                  return (
                    <tr key={item.id} className="hover:bg-black/5 dark:hover:bg-white/5 transition-colors group">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${isOut ? 'bg-red-500' : isLow ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                          <div>
                            <p className="font-bold leading-tight">{item.name}</p>
                            {item.sku && <p className="text-[10px] text-muted mt-0.5 font-medium">{item.sku}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.category ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-surface-muted px-2 py-1 text-[10px] font-medium text-muted border border-divider">
                            <Tag size={10} /> {item.category}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted opacity-50">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className={`font-bold text-base ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : ''}`}>
                            {currentStock.toFixed(2)} <span className="text-xs font-normal text-muted">{item.unit}</span>
                          </span>
                          <span className="text-[10px] text-muted mt-0.5">Mín: {minStock}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-emerald-600 dark:text-emerald-400">
                            ${Number(item.cost || 0).toLocaleString('es-CO')} <span className="text-[10px] font-normal text-muted">/{item.unit}</span>
                          </span>
                          {item.purchase_unit && (
                            <span className="text-[10px] text-muted mt-0.5">
                              Paca: {item.purchase_unit} x {item.conversion_factor}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleOpenStockAdjust(item)} className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition" title="Corregir Stock">
                            <SlidersHorizontal size={16} />
                          </button>
                          <button onClick={() => setKardexItemId(item.id)} className="p-1.5 text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] rounded-lg transition" title="Ver Kardex">
                            <Layers size={16} />
                          </button>
                          <button onClick={() => setHistoryItem(item)} className="p-1.5 text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] rounded-lg transition" title="Historial de Costos">
                            <History size={16} />
                          </button>
                          <button onClick={() => { setWasteDefaultItem(item.id); setIsWasteModalOpen(true); }} className="p-1.5 text-muted hover:bg-red-50 hover:text-red-600 rounded-lg transition" title="Registrar Merma">
                            <Trash2 size={16} />
                          </button>
                          <button onClick={() => handleEdit(item)} className="p-1.5 text-muted hover:bg-primary/10 hover:text-primary rounded-lg transition" title="Editar Insumo">
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Paginador Inferior ── */}
        {filtered.length > pageSize && (
          <div className="rounded-2xl border border-divider bg-surface p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs text-muted">
              Página <strong className="text-[color:var(--color-text)]">{validCurrentPage}</strong> de{' '}
              <strong className="text-[color:var(--color-text)]">{totalPages}</strong> ({filtered.length} insumos totales)
            </span>

            <div className="flex items-center gap-1.5">
              {/* Primera Página */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={validCurrentPage === 1}
                className="rounded-xl border border-divider p-2 text-muted hover:text-[color:var(--color-text)] hover:bg-surface-muted disabled:opacity-30 disabled:pointer-events-none transition"
                title="Primera página"
              >
                <ChevronsLeft size={16} />
              </button>

              {/* Anterior */}
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={validCurrentPage === 1}
                className="rounded-xl border border-divider px-3 py-1.5 text-xs font-bold text-muted hover:text-[color:var(--color-text)] hover:bg-surface-muted disabled:opacity-30 disabled:pointer-events-none transition flex items-center gap-1"
              >
                <ChevronLeft size={15} /> Anterior
              </button>

              {/* Indicador de Páginas Numéricas */}
              <div className="flex items-center gap-1 px-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - validCurrentPage) <= 1)
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    const showEllipsis = prev && p - prev > 1;

                    return (
                      <React.Fragment key={p}>
                        {showEllipsis && <span className="px-1 text-xs text-muted">...</span>}
                        <button
                          onClick={() => setCurrentPage(p)}
                          className={`h-8 min-w-[32px] rounded-xl px-2 text-xs font-bold transition ${
                            validCurrentPage === p
                              ? 'bg-primary text-white shadow-sm'
                              : 'text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)]'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              {/* Siguiente */}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={validCurrentPage === totalPages}
                className="rounded-xl border border-divider px-3 py-1.5 text-xs font-bold text-muted hover:text-[color:var(--color-text)] hover:bg-surface-muted disabled:opacity-30 disabled:pointer-events-none transition flex items-center gap-1"
              >
                Siguiente <ChevronRight size={15} />
              </button>

              {/* Última Página */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={validCurrentPage === totalPages}
                className="rounded-xl border border-divider p-2 text-muted hover:text-[color:var(--color-text)] hover:bg-surface-muted disabled:opacity-30 disabled:pointer-events-none transition"
                title="Última página"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Modal de Ajuste / Corrección de Stock ── */}
        {stockAdjustItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="w-full max-w-md rounded-2xl bg-surface border border-divider shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-divider px-6 py-4 bg-surface-muted/50">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <SlidersHorizontal size={20} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[color:var(--color-text)]">
                      Ajuste / Corrección de Stock
                    </h3>
                    <p className="text-xs text-muted font-medium">{stockAdjustItem.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setStockAdjustItem(null)}
                  className="rounded-full p-2 text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Banner de Stock Actual */}
                <div className="rounded-xl border border-divider bg-surface-muted/60 p-3.5 flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted block">
                      Stock Actual en Sistema
                    </span>
                    <p className={`text-xl font-extrabold ${
                      Number(stockAdjustItem.current_stock) <= 0 ? 'text-red-600' : 'text-emerald-600'
                    }`}>
                      {Number(stockAdjustItem.current_stock ?? 0).toFixed(2)}{' '}
                      <span className="text-xs font-normal text-muted">{stockAdjustItem.unit}</span>
                    </p>
                  </div>
                  {stockAdjustItem.sku && (
                    <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-lg">
                      {stockAdjustItem.sku}
                    </span>
                  )}
                </div>

                {/* Modos de Ajuste */}
                <div>
                  <label className="mb-2 block text-xs font-bold text-muted uppercase tracking-wider">
                    Tipo de Operación:
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setAdjustMode('COUNT')}
                      className={`rounded-xl border p-2.5 text-center text-xs font-bold transition ${
                        adjustMode === 'COUNT'
                          ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                          : 'border-divider bg-surface text-muted hover:bg-surface-muted'
                      }`}
                    >
                      <Boxes size={16} className="mx-auto mb-1 text-primary" />
                      Conteo Real
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdjustMode('IN')}
                      className={`rounded-xl border p-2.5 text-center text-xs font-bold transition ${
                        adjustMode === 'IN'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-500/20'
                          : 'border-divider bg-surface text-muted hover:bg-surface-muted'
                      }`}
                    >
                      <ArrowUpRight size={16} className="mx-auto mb-1 text-emerald-600" />
                      Añadir (+)
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdjustMode('OUT')}
                      className={`rounded-xl border p-2.5 text-center text-xs font-bold transition ${
                        adjustMode === 'OUT'
                          ? 'border-red-500 bg-red-50 text-red-700 ring-2 ring-red-500/20'
                          : 'border-divider bg-surface text-muted hover:bg-surface-muted'
                      }`}
                    >
                      <ArrowDownRight size={16} className="mx-auto mb-1 text-red-600" />
                      Restar (-)
                    </button>
                  </div>
                </div>

                {/* Input de Cantidad */}
                <div>
                  <label className="mb-1 block text-xs font-bold text-muted">
                    {adjustMode === 'COUNT'
                      ? `Cantidad FÍSICA Real (${stockAdjustItem.unit}) *`
                      : adjustMode === 'IN'
                        ? `Cantidad a SUMAR (${stockAdjustItem.unit}) *`
                        : `Cantidad a RESTAR por merma/desecho (${stockAdjustItem.unit}) *`}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={adjustValue}
                      onChange={e => setAdjustValue(e.target.value)}
                      placeholder="0.00"
                      className="w-full rounded-xl border border-divider bg-surface px-4 py-2.5 text-base font-bold text-[color:var(--color-text)] focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                      autoFocus
                    />
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted">
                      {stockAdjustItem.unit}
                    </span>
                  </div>

                  {/* Resumen Diferencia en Tiempo Real */}
                  {(() => {
                    const current = Number(stockAdjustItem.current_stock ?? 0);
                    const val = parseFloat(adjustValue.replace(',', '.')) || 0;
                    let target = current;
                    if (adjustMode === 'COUNT') target = val;
                    else if (adjustMode === 'IN') target = current + val;
                    else if (adjustMode === 'OUT') target = Math.max(0, current - val);

                    const diff = target - current;

                    return (
                      <div className="mt-2 rounded-lg bg-surface-muted/60 p-2 text-xs flex items-center justify-between border border-divider/60">
                        <span className="text-muted">Nuevo stock resultante:</span>
                        <div className="flex items-center gap-1.5 font-bold">
                          <span className="text-[color:var(--color-text)]">{target.toFixed(2)} {stockAdjustItem.unit}</span>
                          {diff !== 0 && (
                            <span className={`text-[11px] px-1.5 py-0.2 rounded ${
                              diff > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Motivo del Ajuste */}
                <div>
                  <label className="mb-1 block text-xs font-bold text-muted">Motivo del Ajuste *</label>
                  <input
                    type="text"
                    value={adjustReason}
                    onChange={e => setAdjustReason(e.target.value)}
                    placeholder="Ej: Conteo físico fin de semana"
                    className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary shadow-sm mb-2"
                  />

                  {/* Chips de Motivo Rápido */}
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_REASONS.map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setAdjustReason(r)}
                        className={`rounded-lg px-2 py-0.5 text-[10.5px] font-medium transition ${
                          adjustReason === r
                            ? 'bg-primary text-white font-bold'
                            : 'bg-surface-muted text-muted hover:text-[color:var(--color-text)]'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-divider px-6 py-3.5 bg-surface-muted/30 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setStockAdjustItem(null)}
                  className="px-4 py-2 text-xs font-bold text-muted hover:text-[color:var(--color-text)] transition"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveStockAdjust}
                  disabled={adjustStockMut.isPending}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2 text-xs font-bold text-white shadow-md hover:opacity-90 disabled:opacity-60 transition"
                >
                  <Save size={15} />
                  {adjustStockMut.isPending ? 'Guardando ajuste...' : 'Confirmar Ajuste'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal de Historial de Costos ── */}
        {historyItem && (
          <CostHistoryModal
            item={historyItem}
            onClose={() => setHistoryItem(null)}
          />
        )}

        {/* ── Modal de Confirmación de Eliminación ── */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl border border-divider">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600">
                <Trash2 size={24} />
              </div>
              <h3 className="text-lg font-bold text-[color:var(--color-text)]">¿Eliminar insumo?</h3>
              <p className="mt-2 text-xs text-muted leading-relaxed">
                ¿Estás seguro de que deseas eliminar <strong className="text-[color:var(--color-text)]">{confirmDelete.name}</strong>?
                Esta acción removerá el ítem del catálogo activo pero conservará su historial en movimientos previos.
              </p>
              <div className="mt-6 flex justify-end gap-2.5">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="rounded-xl px-4 py-2 text-xs font-bold text-muted hover:bg-surface-muted transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteMut.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-5 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-60 transition shadow-sm"
                >
                  <Trash2 size={14} />
                  {deleteMut.isPending ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal de Gestión de Categorías ── */}
        <CategoryManagerModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onCategoryCreated={(catName) => setForm(f => ({ ...f, category: catName }))}
        />

        {/* ── Modal de Kardex / Trazabilidad Completa ── */}
        <KardexModal
          itemId={kardexItemId}
          onClose={() => setKardexItemId(null)}
        />

        {/* ── Modal de Registro y Control de Mermas ── */}
        <WasteLogModal
          isOpen={isWasteModalOpen}
          onClose={() => { setIsWasteModalOpen(false); setWasteDefaultItem(null); }}
          defaultItemId={wasteDefaultItem}
        />

        {/* ── Modal de Sugerencia Inteligente de Compras ── */}
        <ReplenishmentModal
          isOpen={isReplenishmentOpen}
          onClose={() => setIsReplenishmentOpen(false)}
        />

      </div>
    </div>
  );
};

export default ItemsList;

